import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { ReactSVG } from "react-svg";
import NewToolTip from "../../../../common/New-ToolTip";
import InfoTool from "../../../../common/InfoTool";
import { drugTypes } from "../../service-steps/clinical-assessment/data";
import { AdherenceTypes } from "../../service-steps/medication/data";
import { hasActiveInterventionLink } from "../../../Patient-Menu/Interventions/data";
import { medicationMatchesAmrEducation } from "../../../../../helpers/amrEducationDrugMatch";

const AMRMedicationList = ({
  filteredMedication,
  reviewedMedicationCards,
  medicationType,
  title,
  isExpanded = true,
  onToggleExpanded,
  expandedMedications,
  hoveredMed,
  onMouseEnter,
  onMouseLeave,
  menuMedication,
  onMenuOpen,
  selectedMedication,
  onExpandMedication,
  medicationDetails,
  getNoRecordMessage,
  getCardFromDrug,
  selectMedication,
  isCompleted = false,
}) => {
  const [focusItem, setFocusItem] = useState(null);
  const [showTooltipFor, setShowTooltipFor] = useState(null);
  const tooltipTimeouts = useRef({});

  const handleMedicationNameMouseEnter = (medId) => {
    clearTimeout(tooltipTimeouts.current[medId]);
    tooltipTimeouts.current[medId] = setTimeout(
      () => setShowTooltipFor(medId),
      700
    );
  };

  const handleMedicationNameMouseLeave = (medId) => {
    clearTimeout(tooltipTimeouts.current[medId]);
    delete tooltipTimeouts.current[medId];
    setShowTooltipFor(null);
  };

  const handleMedicationNameDoubleClick = (medId) => {
    clearTimeout(tooltipTimeouts.current[medId]);
    delete tooltipTimeouts.current[medId];
    setShowTooltipFor(medId);
  };

  useEffect(
    () => () => {
      Object.values(tooltipTimeouts.current).forEach(clearTimeout);
    },
    []
  );

  const isCompactHeader = medicationType === drugTypes.Prescription || medicationType === drugTypes.Additional;
  const amrEducation = useSelector((state) => state.clinicalService.amrEducation || []);
  const interventions = useSelector((state) => state.intervention.interventions || []);
  const hasInterventionLink = (record) =>
    hasActiveInterventionLink(record, interventions);
  return (
    <>      
      <div
        className={`cursor-pointer rounded-[4px] flex gap-2 ${isCompactHeader ? "h-[46px] py-0" : "py-[10px]"} pr-[10px] border-[1px] border-gray-100 ${ isExpanded ? "border-b-0" : ""} ${ isExpanded ? "bg-gray-50" : "bg-gray-10" } justify-between items-center mt-1`}
        onClick={onToggleExpanded}
      >
          <div className="flex items-center gap-1 flex-1">
          {isCompleted ? (
            <ReactSVG
              src="/icons/checkmark-square-icon.svg"
              className="w-6 h-6 flex-shrink-0"
            />
          ) : (
            <div className="w-6 h-6 flex-shrink-0" />
          )}
          <div className="flex items-center gap-[6px]">
            <p className={`text-gray-800 text-md ${ isExpanded ? "font-semibold" : "" }`}>{title}</p>
            {isExpanded && (
              <>
                <InfoTool id={`amr-${medicationType}-info`} ariaLabel={`${title} info`} />
                <NewToolTip
                  id={`amr-${medicationType}-info`}
                  render={() => (
                    <div className="bg-gray-10 border border-gray-200 p-2 flex flex-col max-w-[300px]">
                      <p className="text-gray-800 text-sm font-medium">
                        {medicationType === drugTypes.Prescription
                          ? "Prescriptions - Medications filled at the current pharmacy."
                          : medicationType === drugTypes.Vaccines
                          ? "Vaccines - Select Vaccine to record immunizations under the Vaccines tab."
                          : "Additional Medications - OTC, samples, study drugs, natural products, or prescriptions filled at alternate pharmacy."}
                      </p>
                    </div>
                  )}
                />
              </>
            )}
          </div>
        </div>
        <ChevronDownIcon
          className={`w-4 h-4 flex-shrink-0 ${isExpanded ? "-rotate-180" : ""}`}
        />
      </div>
      {isExpanded && (
        <>
          <ul
            className="divide-y divide-gray-100 border-y border-y-gray-100 overflow-y-auto h-full w-full max-h-[600px] pt-3"
            onMouseLeave={() => {
              onMouseLeave?.();
            }}
          >
      {(filteredMedication.filter(
        (x) => x.medicationType === medicationType
      ).length > 0 ||
        reviewedMedicationCards?.filter(
          (x) =>
            x.consumption !== AdherenceTypes.Stopped &&
            x.intervention?.override === "stopped"
        ).length > 0) &&
        //filter records, if it is active medication and should not be reviewed as stopped taking.
        filteredMedication
          ?.filter(
            (x) =>
              x.medicationType === medicationType &&
              (reviewedMedicationCards.length === 0 ||
                !reviewedMedicationCards
                  ?.filter(
                    (f) =>
                      f?.consumption === AdherenceTypes.Stopped &&
                      f?.intervention?.override === "stopped"
                  )
                  .map((i) => i.name)
                  .includes(x.displayName))
          )
          .map((med, i) => (
            <li
              key={med.id}
              className="cursor-pointer text-sm text-gray-800 w-full list-none"
              onClick={(e) => {
                setFocusItem(med.id);
                if (med.status === "active") {
                  selectMedication(med);
                }
                e.currentTarget?.scrollIntoView?.({
                  block: "nearest",
                  behavior: "smooth",
                });
              }}
            >
              {(() => {
                const matchesAmrEducation = medicationMatchesAmrEducation(
                  amrEducation,
                  med
                );
                const isRowSelected =
                  selectedMedication?.id === med.id ||
                  focusItem === med.id ||
                  matchesAmrEducation;
                return (
                  <>
                    <div
                      className={`w-full ${
                        isRowSelected
                          ? "bg-blue-100 hover:bg-blue-100"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div
                        data-medication-tooltip={`${medicationType}-${med.id}`}
                        onMouseEnter={() => {
                          handleMedicationNameMouseEnter(med.id);
                          onMouseEnter(med.id);
                        }}
                        onMouseLeave={() => {
                          handleMedicationNameMouseLeave(med.id);
                          onMouseLeave && onMouseLeave();
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          handleMedicationNameDoubleClick(med.id);
                        }}
                        className={`pr-[10px] flex w-full items-center py-[10px] ${
                          med.status === "inactive" ? "bg-red-50" : ""
                        }`}
                      >
                        <div className="flex flex-shrink-0 w-6 items-center justify-center">
                          {getCardFromDrug(med)?.status === "reviewed" ? (
                            hasInterventionLink(getCardFromDrug(med)) ? (
                              <ReactSVG
                                src="/icons/intervention-sm.svg"
                                className="flex text-black w-4 h-4"
                              />
                            ) : (
                              <ReactSVG
                                src="/icons/correct-tick-sm2.svg"
                                className="flex text-black w-4 h-4"
                              />
                            )
                          ) : getCardFromDrug(med)?.status === "skipped" ? (
                            <ReactSVG
                              src="/icons/skip-sm.svg"
                              className="flex text-black w-4 h-4"
                            />
                          ) : (
                            <span className="w-4 h-4" aria-hidden />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 mr-3 text-left overflow-hidden">
                          <span className="flex items-center min-w-0 gap-[6px] px-[2px]">
                            <span className="block text-left truncate text-gray-800">
                              {med.displayName}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center space-x-3 flex-shrink-0">
                          {((hoveredMed === med.id &&
                            getCardFromDrug(med)?.status !== "reviewed") ||
                            menuMedication?.id === med.id) && (
                            <Menu as="div" className="relative inline-block">
                              <Menu.Button
                                onClick={(e) => {
                                  if (
                                    !reviewedMedicationCards
                                      .map((i) => i.name)
                                      .includes(med.displayName)
                                  ) {
                                    onMenuOpen(e, med);
                                  }
                                }}
                              >
                                <ReactSVG
                                  src="/icons/triple-dot.svg"
                                  className={`flex !items-center py-2 px-3 rounded-md ${
                                    med.id === menuMedication?.id && "bg-gray-300"
                                  }`}
                                />
                              </Menu.Button>
                            </Menu>
                          )}
                          <button
                            type="button"
                            className=""
                            onClick={(e) => {
                              e.stopPropagation();
                              onExpandMedication(med.id);
                            }}
                          >
                            {!expandedMedications.includes(med.id) && (
                              <ChevronDownIcon
                                className="w-4 h-4"
                                strokeWidth={2}
                              />
                            )}
                            {expandedMedications.includes(med.id) && (
                              <ChevronUpIcon
                                className="w-4 h-4"
                                strokeWidth={2}
                              />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    {expandedMedications.includes(med.id) &&
                      medicationDetails(med)}
                    <NewToolTip
                      id={`amr-med-name-${medicationType}-${med.id}`}
                      anchorSelect={`[data-medication-tooltip="${medicationType}-${med.id}"]`}
                      place="bottom-start"
                      isOpen={showTooltipFor === med.id}
                      render={() => (
                        <div className="bg-teal-25 p-2 max-w-md flex flex-col">
                          <p className="text-teal-300 text-left text-body-sm font-normal">
                            {med.displayName}
                          </p>
                          {((med?.manufacturerStoredType === "Small" &&
                            med?.manufacturerCode) ||
                            (med?.manufacturerStoredType === "Full" &&
                              med?.manufacturerName)) && (
                            <p className="text-teal-300 text-left text-body-sm font-normal">
                              {med?.manufacturerStoredType === "Small" &&
                              med?.manufacturerCode
                                ? ` (${med?.manufacturerCode})`
                                : med?.manufacturerStoredType === "Full" &&
                                    med?.manufacturerName
                                  ? ` (${med?.manufacturerName})`
                                  : ""}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </>
                );
              })()}
            </li>
          ))}
          </ul>
        </>
      )}
    </>
  );
};

export default AMRMedicationList;
